import { can } from './permissions';
import type { Certificate } from '@/types/certificate';
import type { Domain } from '@/types/domain';
import type { NginxConfig } from '@/types/nginx';
import type { OriginGroup } from '@/types/originGroup';
import type { Task } from '@/types/task';
import type { UserRole } from '@/types/user';

/**
 * State rules (which operations make sense for an item in its current state)
 * combined with role permissions. Tables render buttons from these flags
 * instead of inlining `role && status === ...` checks in JSX.
 */

type Role = UserRole | null | undefined;

/**
 * Certificate state rules
 */
const certificateRules = {
  /** Stored content can be inspected */
  canView: (cert: Pick<Certificate, 'has_content'>) => cert.has_content,
  /** Active certificate issued on a node whose content is not stored yet */
  canSync: (cert: Pick<Certificate, 'status' | 'has_content' | 'node_id'>) =>
    cert.status === 'active' && !cert.has_content && !!cert.node_id,
  canRenew: (cert: Pick<Certificate, 'status'>) => cert.status === 'active',
  canRetry: (cert: Pick<Certificate, 'status'>) => cert.status === 'error',
};

export interface CertificateActions {
  view: boolean;
  sync: boolean;
  renew: boolean;
  retry: boolean;
  delete: boolean;
}

export function getCertificateActions(role: Role, cert: Certificate): CertificateActions {
  return {
    view: certificateRules.canView(cert),
    sync: can(role, 'certificates:sync') && certificateRules.canSync(cert),
    renew: can(role, 'certificates:renew') && certificateRules.canRenew(cert),
    retry: can(role, 'certificates:retry') && certificateRules.canRetry(cert),
    delete: can(role, 'certificates:delete'),
  };
}

/**
 * Task state rules
 */
export const taskRules = {
  /** Still executing (a retried task runs again in the background) */
  isActive: (task: Pick<Task, 'status'>) => task.status === 'pending' || task.status === 'running',
  canCancel: (task: Pick<Task, 'status'>) => task.status === 'pending' || task.status === 'running',
  canRetry: (task: Pick<Task, 'status'>) =>
    task.status === 'failed' || task.status === 'partially_completed',
};

export function getTaskActions(role: Role, task: Task): { cancel: boolean; retry: boolean } {
  return {
    cancel: can(role, 'tasks:cancel') && taskRules.canCancel(task),
    retry: can(role, 'tasks:retry') && taskRules.canRetry(task),
  };
}

/**
 * Origin group state rules
 */
const originGroupRules = {
  /** Groups still referenced by domains cannot be deleted */
  canDelete: (og: Pick<OriginGroup, 'domain_count'>) => og.domain_count === 0,
};

export function getOriginGroupActions(
  role: Role,
  og: OriginGroup
): { test: boolean; edit: boolean; delete: boolean; deleteBlocked: boolean } {
  const mayDelete = can(role, 'origin-groups:delete');
  return {
    test: can(role, 'origin-groups:test'),
    edit: can(role, 'origin-groups:update'),
    delete: mayDelete,
    deleteBlocked: mayDelete && !originGroupRules.canDelete(og),
  };
}

/**
 * NGINX config state rules
 */
const nginxConfigRules = {
  canActivate: (config: Pick<NginxConfig, 'is_active'>) => !config.is_active,
};

export function getNginxConfigActions(
  role: Role,
  config: NginxConfig
): { activate: boolean; regenerate: boolean; delete: boolean } {
  return {
    activate: can(role, 'nginx:activate') && nginxConfigRules.canActivate(config),
    regenerate: can(role, 'nginx:regenerate'),
    delete: can(role, 'nginx:delete'),
  };
}

/**
 * Domain state rules
 */
export const domainRules = {
  needsDnsValidation: (domain: Pick<Domain, 'dns_validated'>) => !domain.dns_validated,
  /** Eligible for certificate issuance: DNS validated and served by at least one node */
  canIssueCertificate: (domain: Pick<Domain, 'dns_validated' | 'node_ids'>) =>
    domain.dns_validated && (domain.node_ids?.length ?? 0) > 0,
  /** Assigned to at least one node */
  isAssigned: (domain: Pick<Domain, 'node_ids'>) => (domain.node_ids?.length ?? 0) > 0,
};

export function getDomainActions(
  role: Role,
  domain: Domain
): { validateDns: boolean; edit: boolean; delete: boolean } {
  return {
    validateDns: can(role, 'domains:validate-dns') && domainRules.needsDnsValidation(domain),
    edit: can(role, 'domains:update'),
    delete: can(role, 'domains:delete'),
  };
}

/**
 * Certificate expiry thresholds (days)
 */
const EXPIRY_THRESHOLDS = {
  /** Below this a certificate is "expiring soon" */
  warningDays: 30,
  /** Below this the expiry is critical */
  criticalDays: 7,
} as const;

export type ExpiryLevel = 'unknown' | 'expired' | 'critical' | 'warning' | 'ok';

/**
 * Classify the number of days until expiry
 */
export function getExpiryLevel(daysUntilExpiry: number | null): ExpiryLevel {
  if (daysUntilExpiry === null) return 'unknown';
  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry <= EXPIRY_THRESHOLDS.criticalDays) return 'critical';
  if (daysUntilExpiry <= EXPIRY_THRESHOLDS.warningDays) return 'warning';
  return 'ok';
}
